import { RemoveButton, SaveButton } from "components/Buttons"
import "components/style.scss"
import React, { Fragment, useEffect, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
import ReactSwitch from "react-switch"
import {
  Button,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
} from "reactstrap"
import { getSelectGroupOptions } from "utils"
import { getFileBytes } from "../utils/index"
import { ContentState, convertToRaw, EditorState } from "draft-js"
import { Editor } from "react-draft-wysiwyg"
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"
import "flatpickr/dist/themes/material_green.css"
import FilerobotImageEditor from "filerobot-image-editor"
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete"

import Flatpickr from "react-flatpickr"
import draftToHtml from "draftjs-to-html"
import htmlToDraft from "html-to-draftjs"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"

function FileListItems(files) {
  var b = new ClipboardEvent("").clipboardData || new DataTransfer()
  for (var i = 0, len = files.length; i < len; i++) b.items.add(files[i])
  return b.files
}

export const TextField = ({
  input,
  label,
  placeholder = label,
  type = "text",
  meta: { touched, error, submitFailed },
  callback,
  disabled,
  min,
  max,
  maxLength,
  step,
  className,
  digitsOnly = false
}) => {
  useEffect(() => {
    if (input?.value?.length > maxLength) {
      input.onChange(input.value.slice(0, maxLength))
    }
  }, [maxLength])
  
  const blockNonDigits = (e) => { 
    if ( 
      !e.key.match(/^[0-9]$/) && 
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key) 
    ) { 
      e.preventDefault() 
    } 
  }

  return (
    <div>
      {label && <label>{label}</label>}
      <div>
        <input
          placeholder={placeholder}
          min={min}
          max={max}
          type={type}
          className={className ? `${className} form-control` : "form-control"}
          {...input}
          {...(disabled ? { disabled } : {})}
          maxLength={maxLength}
          step={step}
          onKeyDown={digitsOnly ? blockNonDigits : undefined}
        />
        {maxLength && (
          <span style={{ float: "right" }}>
            {input.value.length}/{maxLength}
          </span>
        )}
        {(touched || submitFailed) && error && <InputError error={error} />}
      </div>
    </div>
  )
}

export const FileField = ({
  input,
  label,
  meta: { touched, error },
  onClick,
  accept,
}) => {
  const fileInput = useRef()
  const [shouldRenderRemove, setShouldRenderRemove] = useState(
    fileInput &&
      fileInput.current &&
      fileInput.current.files &&
      fileInput.current.files.length > 0
  )

  useEffect(() => {
    if (
      input.value.dataURI &&
      !(
        fileInput &&
        fileInput.current &&
        fileInput.current.files &&
        fileInput.current.files.length > 0
      )
    ) {
      if (fileInput.current) {
        fileInput.current.files = new FileListItems([
          new File([], input.value.dataURI),
        ])
        setShouldRenderRemove(true)
      }
    }
  }, [input.value])

  const handleChange = ({
    target: {
      files: [file],
    },
  }) => {
    getFileBytes(file)
      .then(dataURI => {
        setShouldRenderRemove(true)
        if (input.onChange) {
          input.onChange({
            dataURI,
            name: file.name,
            type: file.type,
          })
        }
      })
      .catch(() => {
        setShouldRenderRemove(false)
        if (fileInput && fileInput.current) {
          fileInput.current.value = null
        }
        if (input.onChange) {
          input.onChange()
        }
      })
  }

  const reset = () => {
    handleChange({ target: { files: [] } })
  }

  return (
    <>
      {label && <label>{label}</label>}
      <InputGroup onClick={onClick}>
        <Input
          innerRef={fileInput}
          type="file"
          className="form-control"
          accept={accept}
          onChange={handleChange}
        />
        {shouldRenderRemove && (
          <InputGroupAddon addonType="prepend">
            <Button color="danger" type="button" onClick={reset}>
              Remove
            </Button>
          </InputGroupAddon>
        )}
      </InputGroup>
      {touched && error && <InputError error={error} />}
    </>
  )
}

export const SingleSelectField = props => {
  const {
    input,
    label,
    placeholder = label,
    options,
    meta: { touched, error, submitFailed },
    disabled,
    isSearchable,
  } = props

  let defaultValue = null
  if (options && options.length > 0) {
    defaultValue = options.find(opt => opt.value === input.value)
  }

  const [selectedOption, setSelectedOption] = useState(defaultValue)

  useEffect(() => {
    if (defaultValue && !selectedOption) {
      setSelectedOption(defaultValue)
    } else if (
      defaultValue &&
      selectedOption &&
      defaultValue?.value !== selectedOption?.value
    ) {
      setSelectedOption(defaultValue)
    }
  }, [defaultValue])

  useEffect(() => {
    if (!input.value && input?.value !== 0) {
      setSelectedOption(null)
    }
  }, [input.value])

  function handleSelectGroup(selectedOption) {
    setSelectedOption(selectedOption)
    input.onChange(selectedOption.value)
  }
  return (
    <div>
      {label && (
        <label className={disabled ? "disabled-label" : ""}>{label}</label>
      )}
      <div>
        <Select
          value={selectedOption}
          onChange={v => handleSelectGroup(v)}
          options={options}
          classNamePrefix="select2-selection"
          placeholder={placeholder}
          isDisabled={disabled}
          isSearchable={isSearchable}
          onFocus={input.onFocus}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
        {(touched || submitFailed) && error && <InputError error={error} />}
      </div>
    </div>
  )
}

export const MultipleSelectField = ({
  input,
  label,
  placeholder = label,
  options,
  meta: { touched, error },
  disabled,
}) => {
  let defaultValues = null
  if (options && options.length > 0) {
    defaultValues = options.filter(opt => input.value.includes(opt.value))
  }
  const [selectedOptions, setSelectedGroup] = useState(defaultValues)

  useEffect(() => {
    if (
      defaultValues &&
      defaultValues?.length &&
      (!selectedOptions || !selectedOptions?.length)
    ) {
      setSelectedGroup(defaultValues)
    }
  }, [defaultValues])

  useEffect(() => {
    if (!input.value) {
      setSelectedGroup(null)
    }
  }, [input.value])

  function handleMulti(selectedOptions) {
    setSelectedGroup(selectedOptions)
    let inputArrayValue = []
    if (selectedOptions && selectedOptions.length > 0) {
      for (let i = 0; i < selectedOptions.length; i++) {
        inputArrayValue.push(selectedOptions[i].value)
      }
    }
    input.onChange(inputArrayValue)
  }

  return (
    <div>
      {label && (
        <label className={disabled ? "disabled-label" : ""}>{label}</label>
      )}
      <div>
        <Select
          value={selectedOptions}
          isMulti={true}
          onChange={v => {
            handleMulti(v)
          }}
          options={options}
          classNamePrefix="select2-selection"
          placeholder={placeholder}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
        {touched && error && <InputError error={error} />}
      </div>
    </div>
  )
}
export const TextAreaField = ({
  input,
  label,
  rows = 4,
  meta: { touched, error },
}) => (
  <div>
    {label && <label>{label}</label>}
    <div>
      <textarea {...input} rows={rows} className="form-control" />
      {touched && error && <InputError error={error} />}
    </div>
  </div>
)

export const CheckboxField = ({
  input,
  label,
  disabled,
  meta: { touched, error },
}) => (
  <div className="form-check">
    <label className="form-check-label">
      <input
        {...input}
        checked={input.value}
        type="checkbox"
        className="form-check-input"
        disabled={disabled}
      />
      {label}
    </label>
    {touched && error && <InputError error={error} />}
  </div>
)

export const SwitchField = ({ input, label, meta: { touched, error } }) => {
  return (
    <div className="form-check form-switch form-switch-md">
      <input
        {...input}
        checked={input.value}
        type="checkbox"
        className="form-check-input"
      />
      <label className="form-check-label">{label}</label>
      {touched && error && <InputError error={error} />}
    </div>
  )
}

export const RadioField = ({
  input,
  label,
  meta: { touched, error },
  disabled,
}) => {
  return (
    <div className="form-check form-radio-outline form-radio-primary">
      <label className="form-check-label">
        <input
          {...input}
          type="radio"
          className="form-check-input"
          disabled={disabled}
        />
        {label}
      </label>
      {touched && error && <InputError error={error} />}
    </div>
  )
}

export const ImageDropzoneField = ({
  input,
  label = "Drop file here or click to upload.",
  meta: { touched, error },
}) => {
  const [selectedFiles, setSelectedFiles] = useState([])

  if (typeof input.value === "object" && input.value.file) {
    input.value = input.value.file.url
  }

  function handleAcceptedFiles(files) {
    files.map(file =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    )
    setSelectedFiles(files)
    if (files.length > 0) {
      input.onChange(files)
    } else {
      input.onChange(null)
    }
  }

  /**
   * Formats the size
   */
  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  return (
    <Fragment>
      {selectedFiles && selectedFiles.length > 0 ? (
        <div className="dropzone-previews mt-3" id="file-previews">
          {selectedFiles.map((f, i) => {
            return (
              <Fragment key={i + "-file"}>
                <img
                  // data-dz-thumbnail=""
                  height="200"
                  width="200"
                  // className="avatar-lg rounded bg-light"
                  className="rounded bg-light"
                  alt={f.name}
                  src={f.preview}
                />
                <div className={"font-size-11 mt-1"}>
                  <div>{f.name}</div>
                  <div className="mb-0">
                    <strong>{f.formattedSize}</strong>
                  </div>
                </div>
              </Fragment>
            )
          })}
          <div className={"mt-2"}>
            <RemoveButton onClick={() => handleAcceptedFiles([])}>
              Remove image
            </RemoveButton>
          </div>
        </div>
      ) : input.value ? (
        <div className="dropzone-previews mt-3" id="file-previews">
          <img
            // data-dz-thumbnail=""
            height="200"
            width="200"
            // className="avatar-lg rounded bg-light"
            className="rounded bg-light"
            // alt={f.name}
            src={input.value}
          />
          <div className={"mt-2"}>
            <RemoveButton onClick={() => handleAcceptedFiles([])}>
              Remove image
            </RemoveButton>
          </div>
        </div>
      ) : (
        <Dropzone
          maxFiles={1}
          onDrop={acceptedFiles => {
            handleAcceptedFiles(acceptedFiles)
          }}
        >
          {({ getRootProps, getInputProps }) => {
            return (
              <div className="dropzone">
                <div className="dz-message needsclick mt-2" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className="mb-1">
                    <i className="display-6 text-muted bx bxs-cloud-upload" />
                  </div>
                  <h5>{label}</h5>
                </div>
              </div>
            )
          }}
        </Dropzone>
      )}
      {touched && error && <InputError error={error} />}
    </Fragment>
  )
}

export const InputError = ({ error }) => {
  return <span className={"input-error"}>{error}</span>
}

//used without a form, on the header clients drop-down
export const ReactSelectDropDown = ({
  optionData,
  defaultValueId,
  onSelectItem,
}) => {
  let options = getSelectGroupOptions(optionData)

  let defaultValue = {}
  if (options && options.length > 0) {
    defaultValue = options[0].options.find(opt => opt.value === defaultValueId)
  }
  const [selectedGroup, setSelectedGroup] = useState(defaultValue)

  function handleSelectGroup(selectedGroup) {
    setSelectedGroup(selectedGroup)
    onSelectItem(selectedGroup)
  }

  return (
    <Select
      value={selectedGroup}
      onChange={v => {
        handleSelectGroup(v)
      }}
      isSearchable={true}
      options={options}
      classNamePrefix="select2-selection"
      maxMenuHeight={500}
      placeholder={"Select client ..."}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: base => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  )
}

// admin-tags select (creatable)
export const TagsMultiSelect = ({
  input,
  label,
  placeholder = label,
  options,
  meta: { touched, error },
  disabled,
  create = label => ({ value: label, label }),
}) => {
  function handleChange(value) {
    input.onChange(value)
  }

  function handleCreate(label) {
    create(label)
    const newValue = [...input.value, { label }]
    input.onChange(newValue)
  }

  return (
    <div>
      {label && (
        <label className={disabled ? "disabled-label" : ""}>{label}</label>
      )}
      <div>
        <CreatableSelect
          isMulti
          placeholder={placeholder}
          options={options}
          value={input.value}
          onChange={handleChange}
          onCreateOption={handleCreate}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: 9999,
            }),
          }}
        />
        {touched && error && <InputError error={error} />}
      </div>
    </div>
  )
}

export const SwitchInputField = ({
  input,
  label,
  meta: { touched, error },
  uncheckedIcon = true,
  checkedIcon = true,
  onColor = "#080",
  offColor = "#888",
  height = 28,
  width = 56,
  handleDiameter = 25,
  disabled = false,
}) => {
  return (
    <div className="mt-3">
      <label>
        <span>{label}</span>
        <ReactSwitch
          {...input}
          checked={!!input.value}
          className="react-switch"
          uncheckedIcon={uncheckedIcon}
          checkedIcon={checkedIcon}
          onColor={onColor}
          offColor={offColor}
          height={height}
          width={width}
          handleDiameter={handleDiameter}
          disabled={disabled}
        />
      </label>
      {touched && error && <InputError error={error} />}
    </div>
  )
}

export const RichTextField = ({
  backgroundColor,
  textColor,
  backgroundImage,
  input,
  meta: { touched, error },
}) => {
  const [editorState, setEditorState] = useState(() =>
    input?.value
      ? EditorState.createWithContent(
          ContentState.createFromBlockArray(htmlToDraft(input?.value))
        )
      : EditorState.createEmpty()
  )

  useEffect(() => {
    if (
      input?.value !==
      draftToHtml(convertToRaw(editorState.getCurrentContent()))
    ) {
      setEditorState(
        EditorState.createWithContent(
          ContentState.createFromBlockArray(htmlToDraft(input?.value))
        )
      )
    }
  }, [input?.value])

  const onEditorStateChange = editorState => {
    if (input.onChange) {
      input.onChange(draftToHtml(convertToRaw(editorState.getCurrentContent())))
    }
    setEditorState(editorState)
  }

  const toolbarOptions = {
    options: [
      "history",
      "list",
      "textAlign",
      "link",
      "image",
      "emoji",
      "blockType",
      "fontFamily",
      "fontSize",
      "inline",
      "colorPicker",
    ],
    list: {
      inDropdown: true,
    },
    link: {
      popupClassName: "link-modal",
    },
  }

  return (
    <div className="editor-wrapper">
      <Editor
        editorState={editorState}
        toolbar={toolbarOptions}
        wrapperStyle={{
          border: "1px solid lightgrey",
        }}
        onEditorStateChange={onEditorStateChange}
        editorStyle={{
          resize: "vertical",
          overflow: "auto",
          minHeight: "15em",
          backgroundColor: backgroundColor,
          color: textColor,
          backgroundImage: `url("${backgroundImage}")`,
        }}
      />
      {touched && error && <InputError error={error} />}
    </div>
  )
}

export const CheckboxGroup = ({ options, input, meta: { touched, error } }) => (
  <>
    {options.map(option => (
      <div className="form-check mx-2" key={option.value}>
        <label className="form-check-label">
          <input
            className="form-check-input"
            type="checkbox"
            name={option.label}
            disabled={option?.disabled || false}
            checked={input.value.indexOf(option.value) !== -1}
            onChange={event => {
              const newValue = [...input.value]
              if (event.target.checked) {
                newValue.push(option.value)
              } else {
                newValue.splice(newValue.indexOf(option.value), 1)
              }
              return input.onChange(newValue)
            }}
          />
          {option.label}
        </label>
      </div>
    ))}
    {touched && error && <InputError error={error} />}
  </>
)

export const CustomRadioField = ({
  input,
  label,
  img,
  meta: { touched, error },
}) => (
  <div className="layoutSelection">
    <label className="form-check-label">
      <input {...input} type="radio" className="form-check-input" />
      <img src={img} style={{ maxWidth: "120px" }} />
      <h6 className="text-center my-1">{label}</h6>
    </label>
    {touched && error && <InputError error={error} />}
  </div>
)

export const AsyncTypeahedSelect = ({
  input,
  label,
  placeholder = label,
  options,
  meta: { touched, error },
  isMulti = false,
  disabled,
  onInputChange = () => {},
  isCreatable = false,
  inlineValue = true,
  create = label => ({ value: label, label }),
  onMenuClose = () => {},
}) => {
  const [value, setValue] = useState(input.value || [])
  let timeOut = null

  useEffect(() => {
    if (!input.value) {
      setValue([])
    } else if (!value || value?.length === 0) {
      setValue(input.value)
    }
  }, [input.value])

  function handleChange(value) {
    setValue(value)
    input.onChange(value)
  }

  function handleCreate(label) {
    create(label)
    const newValue = [...value, { label }]
    setValue(newValue)
    input.onChange(newValue)
  }

  function handleInputChange(value, meta = {}) {
    if (timeOut) {
      clearTimeout(timeOut)
    }
    timeOut = setTimeout(() => {
      if (meta.action === "input-change") {
        onInputChange(value)
      }
    }, 300)
  }

  const handleRemoveItem = removableItem => {
    handleChange(
      value.filter(item => item.value !== removableItem.value),
      {
        action: "remove-value",
        removableItem,
      }
    )
  }

  return (
    <div>
      {label && (
        <label className={disabled ? "disabled-label" : ""}>{label}</label>
      )}
      <div>
        {isCreatable ? (
          <CreatableSelect
            isMulti={isMulti}
            placeholder={placeholder}
            options={options}
            value={value}
            onChange={handleChange}
            onCreateOption={handleCreate}
            onInputChange={handleInputChange}
            isDisabled={disabled}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({
                ...base,
                zIndex: 9999,
              }),
            }}
          />
        ) : (
          <>
            <Select
              isMulti={isMulti}
              placeholder={placeholder}
              options={options}
              value={value}
              onChange={handleChange}
              onInputChange={handleInputChange}
              controlShouldRenderValue={inlineValue}
              selectProps="aria-label"
              noOptionsMessage={e => (e.inputValue ? "No options" : null)}
              onMenuClose={onMenuClose}
              isDisabled={disabled}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
            {!inlineValue && (
              <ul className="select-list">
                {(value || []).map(item => (
                  <li key={item.value}>
                    {item.label}
                    <i
                      className="bx bx-sm bx-x"
                      onClick={() => handleRemoveItem(item)}
                    ></i>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {touched && error && <InputError error={error} />}
      </div>
    </div>
  )
}

export const RadioGroup = ({ input, label, disabled }) => {
  return (
    <>
      <div className="form-check form-radio-outline form-radio-primary">
        <label className="form-check-label">
          <input
            {...input}
            type="radio"
            className="form-check-input"
            disabled={disabled}
          />
          {label}
        </label>
      </div>
    </>
  )
}

export const errorMessage = ({ meta }) => (
  <div className={"input-error"}>
    {meta.error && meta.touched && `${meta.error}`}
  </div>
)

export const PhoneField = ({
  input,
  label,
  placeholder = label,
  meta: { touched, error, submitFailed },
  callback,
  disabled,
}) => {
  useEffect(() => {
    const phoneNumber = input?.value?.replace(/[A-Za-z]+/g, "")
    input.onChange(phoneNumber)
  }, [input.value])

  return (
    <div>
      {label && <label>{label}</label>}
      <div>
        <input
          placeholder={placeholder}
          className="form-control"
          {...input}
          {...(disabled ? { disabled } : {})}
        />
        {(touched || submitFailed) && error && <InputError error={error} />}
      </div>
    </div>
  )
}

export const CustomImageField = ({
  input,
  label,
  meta: { touched, error },
  disabled,
  onDelete,
  removeButtonDisabled = false,
  buttonSize = "md",
}) => {
  const imageRef = useRef(null)
  const [showImageEditor, setShowImageEditor] = useState(false)
  const [imageFile, setImageFile] = useState(null)
  const [shouldRenderRemove, setShouldRenderRemove] = useState(
    imageRef &&
      imageRef.current &&
      imageRef.current.files &&
      imageRef.current.files.length > 0
  )

  useEffect(() => {
    if (input?.value?.editedImage || input?.value?.originalImage) {
      if (imageRef.current) {
        imageRef.current.files = new FileListItems([
          new File([], input.value.editedImage || input.value.originalImage),
        ])
        setShouldRenderRemove(true)
      }
    }
    return () => {
      setImageFile(null)
      setShouldRenderRemove(null)
    }
  }, [input.value])

  const handleSave = ({ canvas }) => {
    const editedImage = canvas.toDataURL()

    if (input.onChange) {
      input.onChange({
        editedImage,
      })
    }

    if (input.onBlur) {
      input.onBlur()
    }
    setImageFile(null)
    setShowImageEditor(false)
  }

  const handleFileUpload = event => {
    setImageFile(event.target.files[0])
    setShowImageEditor(true)
  }

  const reset = () => {
    setShouldRenderRemove(false)
    setImageFile(null)
    if (input.onChange) {
      input.onChange({})
    }
    if (imageRef && imageRef.current) {
      imageRef.current.value = null
    }
    if (onDelete) {
      onDelete()
    }
  }

  return (
    <React.Fragment>
      {shouldRenderRemove && !removeButtonDisabled ? (
        <SaveButton
          type="button"
          content="Remove"
          onClick={reset}
          color="danger"
          disabled={disabled}
          size={buttonSize}
        />
      ) : (
        <SaveButton
          type="button"
          content="Browse"
          onClick={() => {
            imageRef.current.click()
          }}
          disabled={disabled}
          size={buttonSize}
        />
      )}
      <input
        ref={imageRef}
        onChange={handleFileUpload}
        type="file"
        accept="image/*"
        className="d-none"
      />
      <FilerobotImageEditor
        show={showImageEditor}
        src={imageFile}
        onClose={() => {
          setImageFile(null)
          setShowImageEditor(false)
        }}
        onComplete={handleSave}
        config={{
          colorScheme: "light",
          finishButtonLabel: "Save",
        }}
        onBeforeComplete={() => {
          return false
        }}
      />
    </React.Fragment>
  )
}

export const InputGroupField = ({
  input,
  label,
  placeholder = label,
  type = "text",
  meta: { touched, error, submitFailed },
  disabled,
  min,
  max,
  step,
  inputGroupText,
}) => {
  return (
    <div>
      {label && <label>{label}</label>}
      <div>
        <InputGroup>
          {inputGroupText && (
            <InputGroupAddon addonType="prepend">
              <InputGroupText>{inputGroupText}</InputGroupText>
            </InputGroupAddon>
          )}
          <Input
            type={type}
            className="form-control"
            placeholder={placeholder}
            min={min}
            max={max}
            {...(disabled ? { disabled } : {})}
            {...input}
            step={step}
          />
        </InputGroup>
        {(touched || submitFailed) && error && <InputError error={error} />}
      </div>
    </div>
  )
}

export const FloatingLabelForm = ({
  input,
  label,
  placeholder = label,
  type = "text",
  step = "1",
  max,
  min,
  meta: { touched, error, submitFailed },
}) => {
  return (
    <div>
      <div className="form-floating">
        <Input
          type={type}
          className="form-control"
          placeholder={placeholder}
          step={step}
          max={max}
          min={min}
          {...input}
        />
        <Label size="sm">{label}</Label>
      </div>
      {(touched || submitFailed) && error && <InputError error={error} />}
    </div>
  )
}

export const LocationAutocomplete = ({
  input = {},
  label,
  meta: { touched, error },
  change,
  updateLat,
  updateLng,
  placeholder = "Search address..",
}) => {
  const [coordinates, setCoordinates] = useState({
    lat: null,
    lng: null,
  })

  const handleSelect = async value => {
    const results = await geocodeByAddress(value)
    const latLng = await getLatLng(results[0])
    setCoordinates(latLng)
    updateLat(latLng.lat)
    updateLng(latLng.lng)
    input.onChange(value)
  }

  const handleChange = address => {
    input.onChange(address)
    setCoordinates(null)
  }

  return (
    <PlacesAutocomplete
      value={input.value}
      onSelect={handleSelect}
      onChange={handleChange}
      onError={(status, clearSuggestions) => {
        console.log("Google Maps API returned error with status: ", status)
        clearSuggestions()
      }}
      highlightFirstSuggestion={true}
      shouldFetchSuggestions={input.value.length > 2}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div>
          <Row>
            <Col xs={input.value.length > 0 ? 11 : 12}>
              <input
                {...getInputProps({
                  className: "form-control location-search-input",
                })}
                placeholder={placeholder}
              />
              <div className="autocomplete-dropdown-container">
                {loading && <div>Loading...</div>}
                {suggestions.map((suggestion, index) => {
                  const className = suggestion.active
                    ? "suggestion-item--active"
                    : "suggestion-item"
                  const style = suggestion.active
                    ? { backgroundColor: "#fafafa", cursor: "pointer" }
                    : { backgroundColor: "#ffffff", cursor: "pointer" }
                  return (
                    <div
                      key={index}
                      {...getSuggestionItemProps(suggestion, {
                        className,
                        style,
                      })}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  )
                })}
              </div>
            </Col>
            {input.value.length > 0 && (
              <Col xs={1}>
                <i
                  className="bx bx-sm bx-x"
                  style={{
                    paddingLeft: "5px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    input.onChange("")
                    updateLat("")
                    updateLng("")
                  }}
                />
              </Col>
            )}
          </Row>
          <Row>
            {touched && error && <span className={"input-error"}>{error}</span>}
          </Row>
        </div>
      )}
    </PlacesAutocomplete>
  )
}

export const CountryCode = ({
  input,
  label,
  placeholder = label,
  meta: { touched, error, submitFailed },
  country = "us",
  editable,
  disabled,
  maxWidth,
}) => {
  const [value, setValue] = useState("+1")

  useEffect(() => {
    if (!input?.value) {
      input?.onChange("+1")
    }
  }, [])

  useEffect(() => {
    if (input?.value) {
      input.onChange(input?.value?.toString())
      setValue(input?.value?.toString())
    }
  }, [input?.value])

  return (
    <div>
      {label && <label>{label}</label>}
      <div>
        <PhoneInput
          placeholder={placeholder}
          countryCodeEditable={editable}
          country={country}
          inputProps={{ disabled: disabled }}
          inputStyle={{
            maxWidth: maxWidth ? maxWidth : "",
            cursor: "default",
            background: "white",
          }}
          onChange={value => {
            input.onChange(value?.toString())
            setValue(value?.toString())
          }}
          value={value}
        />
        {(touched || submitFailed) && error && <InputError error={error} />}
      </div>
    </div>
  )
}
